import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { LoadingOverlay } from '../components/LoadingOverlay';
import { Wizard } from '../onboarding/Wizard';
import { OrgInfo, orgInfoIsValid } from '../onboarding/steps/OrgInfo';
import { Sport, sportIsValid } from '../onboarding/steps/Sport';
import { Import, importIsValid } from '../onboarding/steps/Import';
import { Locations, locationsIsValid } from '../onboarding/steps/Locations';
import { Payments, paymentsIsValid } from '../onboarding/steps/Payments';
import type { OnboardingDraft, WizardStep } from '../onboarding/types';
import { emptyLocationImportState } from '../components/LocationImportFlow';
import { detectBrowserCountry, detectBrowserLanguage } from '../utils/locale';
import { clientLog } from '../lib/clientLog';
import { findCountry } from '../data/countries';
import { checkOrganizationName, createOrganization } from '../api/orgs';
import { useConfirm } from '../components/ConfirmDialog';
import {
  bulkImport,
  translateRoleValues,
  validateImport,
  type ImportRow,
} from '../api/members';
import { bulkImportLocations } from '../api/locations';
import { startStripeOnboarding } from '../api/billing';
import { fetchCountryFromIP } from '../api/geo';
import { ApiError } from '../api/client';
import { transformRows, uniqueRoleValuesFromRows } from '../lib/memberImport';
import { transformLocationRows } from '../lib/locationImport';
import logoUrl from '../assets/logo-sportadmin.svg';
import logoLimeUrl from '../assets/logo-sportadmin-lime.svg';

// Onboarding is the new-org flow: a multi-step wizard that lands the user
// on /orgs/<slug> on success. Steps are declared here so the page acts as
// the wiring layer; adding/reordering is one-line edits.

const steps: readonly WizardStep[] = [
  {
    id: 'org-info',
    titleKey: 'onboarding.orgInfo.title',
    descriptionKey: 'onboarding.orgInfo.description',
    Component: OrgInfo,
    isValid: orgInfoIsValid,
  },
  {
    id: 'sport',
    titleKey: 'onboarding.sport.title',
    descriptionKey: 'onboarding.sport.description',
    Component: Sport,
    isValid: sportIsValid,
  },
  {
    id: 'import',
    titleKey: 'onboarding.import.title',
    descriptionKey: 'onboarding.import.description',
    Component: Import,
    isValid: importIsValid,
  },
  {
    id: 'locations',
    titleKey: 'onboarding.locations.title',
    descriptionKey: 'onboarding.locations.description',
    Component: Locations,
    isValid: locationsIsValid,
  },
  {
    id: 'payments',
    titleKey: 'onboarding.payments.title',
    descriptionKey: 'onboarding.payments.description',
    Component: Payments,
    isValid: paymentsIsValid,
  },
];

export function Onboarding() {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const confirm = useConfirm();
  // The "a similar club already exists" interstitial is shown at most once per
  // onboarding session — once the user has decided to continue, a retry of the
  // submit (e.g. after a slug collision) shouldn't re-prompt.
  const nameCheckAcknowledged = useRef(false);

  const [draft, setDraft] = useState<OnboardingDraft>(() => ({
    organizationName: '',
    city: '',
    countryCode: detectBrowserCountry(),
    languageCode: detectBrowserLanguage(),
    sportCode: '',
    officeStreet: '',
    officePostalCode: '',
    officeCity: '',
    officeLatitude: null,
    officeLongitude: null,
    contactEmail: '',
    contactPhone: '',
    orgRegistrationNumber: '',
    addressSearchText: '',
    manualEntry: false,
    importFileName: '',
    importParsedRows: [],
    importParsedHeaders: [],
    importParsedSheetNames: [],
    importMapping: {},
    importSuggestionSource: null,
    importValidationErrors: [],
    importWouldSkip: [],
    locationImport: emptyLocationImportState(),
    connectStripe: null,
  }));

  const update = useCallback((patch: Partial<OnboardingDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
  }, []);

  const [errorCode, setErrorCode] = useState<string | null>(null);
  // When the org was created but a follow-up step (member import, location
  // import, Stripe redirect) failed, we capture the slug so the user can opt
  // out of retrying and just continue to the dashboard. Without this they'd
  // be stuck — re-clicking Finish hits createOrganization which now 409s on
  // the taken slug, and that error masks the real problem upstream.
  const [createdOrgSlug, setCreatedOrgSlug] = useState<string | null>(null);
  // Imperative signal to bounce the wizard back to a specific step. Set when a
  // late preflight (member-import validation) surfaces fixable row errors: the
  // per-row error UI lives only in the Import step, so without this the user
  // would sit on the Payments step with no visible feedback (the original
  // "Slutför does nothing" bug).
  const [jumpToStep, setJumpToStep] = useState<{ stepId: string; nonce: number } | null>(null);
  // Tracked separately from the Wizard's internal submitting state so we can
  // render the full-screen LoadingOverlay above everything (including the
  // wizard chrome) while the org-create + import pipeline runs. Big imports
  // can take 5-30s; the overlay communicates "we're working" much more
  // clearly than just disabling the Slutför button.
  const [submitting, setSubmitting] = useState(false);

  // Country fallback: if the browser locale didn't carry a region (e.g. plain
  // "sv" instead of "sv-SE"), ask the API for a server-side IP→country guess
  // (Cloudflare CF-IPCountry header). Best-effort — failures are silent and
  // we never overwrite a code the user has already chosen.
  const token = auth.user?.access_token ?? null;
  useEffect(() => {
    if (draft.countryCode) return;
    let cancelled = false;
    void fetchCountryFromIP(token).then((code) => {
      if (cancelled || !code || !findCountry(code)) return;
      setDraft((d) => (d.countryCode ? d : { ...d, countryCode: code }));
    });
    return () => {
      cancelled = true;
    };
    // Run once on mount; we deliberately don't re-run when the user clears
    // the field manually (that would feel like the form fighting them).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear the "fix your member file" banner the moment the user acts on it.
  // Picking a new file or changing the column mapping resets
  // importValidationErrors to [] (see Import.tsx), so that empty transition is
  // the signal the stale banner should disappear. Scoped to this one code so a
  // file change doesn't wipe an unrelated error (e.g. a Stripe failure).
  useEffect(() => {
    if (errorCode === 'member_validation_failed' && draft.importValidationErrors.length === 0) {
      setErrorCode(null);
    }
  }, [errorCode, draft.importValidationErrors.length]);

  const submit = async (draft: OnboardingDraft) => {
    setErrorCode(null);
    setSubmitting(true);
    try {
      await runSubmit(draft);
    } finally {
      // Reset submitting on every exit path so the LoadingOverlay closes
      // even when validation errors short-circuit the flow. On the happy
      // path navigate() unmounts the page so this is a no-op then.
      setSubmitting(false);
    }
  };

  const runSubmit = async (draft: OnboardingDraft) => {
    const token = auth.user?.access_token ?? null;

    // 1. AI value-translation BEFORE org-create so a translate-values
    //    failure doesn't leave us with a created org and no members. The
    //    transform itself is pure / can't fail, but the network round-trip
    //    can. Best-effort: if the AI call errors we fall back to raw values
    //    and let the backend's static synonym map handle them.
    let canonicalRows: ImportRow[] = [];
    if (draft.importParsedRows.length > 0) {
      const uniqueValues = uniqueRoleValuesFromRows(draft.importParsedRows, draft.importMapping);
      let translations = [] as Awaited<ReturnType<typeof translateRoleValues>>;
      if (uniqueValues.length > 0) {
        try {
          translations = await translateRoleValues(token, uniqueValues);
        } catch (err) {
          clientLog.error('onboarding: translateRoleValues failed; sending raw values', {
            error: err,
            fields: { step: 'onboarding/import', stage: 'translate_role_values' },
          });
        }
      }
      canonicalRows = transformRows(draft.importParsedRows, draft.importMapping, translations);
    }

    // 2. Preflight the import BEFORE creating the org. Validation errors are
    //    blocking — we surface them in the import step and abort. Skip notices
    //    are informational and pass through to the actual import. Done before
    //    createOrganization so we never strand the user with a half-finished
    //    onboarding (org created, no members, validation errors hidden).
    if (canonicalRows.length > 0) {
      try {
        const preflight = await validateImport(token, canonicalRows);
        // Defensive ?? [] — older API builds returned `null` instead of
        // `[]` when there were no findings (Go nil slice → JSON null),
        // and reading .length on null crashes the wizard mid-onboarding.
        // Server is fixed, but keep this so future regressions don't
        // take the page down again.
        const validationErrors = preflight.validation_errors ?? [];
        const wouldSkip = preflight.would_skip ?? [];
        update({
          importValidationErrors: validationErrors,
          importWouldSkip: wouldSkip,
        });
        if (validationErrors.length > 0) {
          // Fixable row errors. The detailed per-row UI only renders in the
          // Import step, and we're on the last step, so without surfacing this
          // the Slutför click looks like a no-op (the bug this branch used to
          // cause). Log it (→ Seq, since it's an error level), show a banner,
          // and bounce the wizard back to the Import step where the row detail
          // is already wired to render.
          clientLog.error('onboarding: member import preflight returned validation errors', {
            fields: {
              step: 'onboarding/import',
              stage: 'preflight_validation_errors',
              errorCount: validationErrors.length,
              skipCount: wouldSkip.length,
            },
          });
          setErrorCode('member_validation_failed');
          setJumpToStep({ stepId: 'import', nonce: Date.now() });
          return;
        }
      } catch (err) {
        // Preflight failures split two ways:
        //   - ApiError (4xx/5xx with code) → the server told us something is
        //     wrong with the request. Falling through to bulkImport would
        //     just produce the same error after we've already created the
        //     org. Surface immediately and abort.
        //   - Anything else (network, JSON parse, …) → genuinely transient.
        //     Log and fall through; the real import runs server-side checks.
        clientLog.error('onboarding: preflight validateImport failed', {
          error: err,
          fields: {
            step: 'onboarding/import',
            stage: 'preflight',
            errorType: err instanceof ApiError ? 'api_error' : 'network_or_parse',
            apiCode: err instanceof ApiError ? err.code : null,
            apiStatus: err instanceof ApiError ? err.status : null,
          },
        });
        if (err instanceof ApiError) {
          setErrorCode(err.code || 'preflight_failed');
          return;
        }
      }
    }

    // 2b. Impersonation guard: warn (non-blocking) when the proposed name is
    //     confusably similar to an established club. Best-effort — a failed
    //     check must never block onboarding, so we swallow errors and proceed.
    //     Only prompt once per session (nameCheckAcknowledged).
    if (!nameCheckAcknowledged.current) {
      let matches: Awaited<ReturnType<typeof checkOrganizationName>> = [];
      try {
        matches = await checkOrganizationName(
          token,
          draft.organizationName.trim(),
          draft.countryCode,
        );
      } catch (err) {
        clientLog.error('onboarding: name-check failed; proceeding', {
          error: err,
          fields: { step: 'onboarding/submit', stage: 'name_check' },
        });
      }
      if (matches.length > 0) {
        const proceed = await confirm({
          title: t('onboarding.nameCheck.title'),
          tone: 'destructive',
          confirmLabel: t('onboarding.nameCheck.confirm'),
          cancelLabel: t('onboarding.nameCheck.cancel'),
          body: (
            <div className="flex flex-col gap-3">
              <p>{t('onboarding.nameCheck.body', { count: matches.length })}</p>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('onboarding.nameCheck.listLabel')}
                </span>
                <ul className="flex flex-col gap-0.5">
                  {matches.map((m) => (
                    <li key={m.slug} className="text-sm font-medium text-foreground">
                      {m.name}
                      {m.city ? <span className="text-muted-foreground"> · {m.city}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ),
        });
        if (!proceed) {
          // User chose to reconsider the name — bounce back to the org-info step.
          setJumpToStep({ stepId: 'org-info', nonce: Date.now() });
          return;
        }
      }
      nameCheckAcknowledged.current = true;
    }

    try {
      const org = await createOrganization(token, {
        name: draft.organizationName.trim(),
        city: draft.city.trim(),
        country_code: draft.countryCode,
        language_code: draft.languageCode,
        sport_code: draft.sportCode || 'other',
        office_street: draft.officeStreet.trim(),
        office_postal_code: draft.officePostalCode.trim(),
        office_city: draft.officeCity.trim(),
        contact_email: draft.contactEmail.trim(),
        contact_phone: draft.contactPhone.trim(),
        org_registration_number: draft.orgRegistrationNumber.trim(),
        // Coordinates only when a place was picked/located — a manual-fallback
        // address (no-key dev) carries none, so the org simply gets no pin.
        ...(draft.officeLatitude != null && draft.officeLongitude != null
          ? { office_latitude: draft.officeLatitude, office_longitude: draft.officeLongitude }
          : {}),
      });

      // The org exists from here on. Capture the slug so the "Continue to
      // dashboard" recovery button below the wizard knows where to go if a
      // follow-up step fails.
      setCreatedOrgSlug(org.slug);

      if (canonicalRows.length > 0) {
        try {
          await bulkImport(token, org.slug, canonicalRows);
        } catch (err) {
          // Org is created — block navigation, show the error, and let the
          // user choose between "Continue to dashboard" (the recovery button
          // we render when createdOrgSlug is set) and digging into the
          // member-import logs. Silently navigating here is what hid the
          // problem from the user originally.
          clientLog.error('onboarding: post-onboarding member import failed', {
            error: err,
            fields: {
              step: 'onboarding/import',
              stage: 'bulk_import',
              errorType: err instanceof ApiError ? 'api_error' : 'network_or_parse',
              apiCode: err instanceof ApiError ? err.code : null,
              apiStatus: err instanceof ApiError ? err.status : null,
            },
            orgSlug: org.slug,
          });
          setErrorCode(
            err instanceof ApiError && err.code
              ? err.code
              : 'member_import_failed',
          );
          return;
        }
      }

      // Location import — same surface-the-error contract as bulk_import
      // above. resolvedRows is populated when the user picked a file in the
      // Locations step; otherwise this block no-ops.
      if (draft.locationImport.resolvedRows.length > 0) {
        try {
          const rows = transformLocationRows(
            draft.locationImport.resolvedRows,
            draft.locationImport.splitOption,
          );
          if (rows.length > 0) {
            await bulkImportLocations(token, org.slug, rows);
          }
        } catch (err) {
          clientLog.error('onboarding: post-onboarding location import failed', {
            error: err,
            fields: {
              step: 'onboarding/locations',
              stage: 'bulk_import_locations',
              errorType: err instanceof ApiError ? 'api_error' : 'network_or_parse',
              apiCode: err instanceof ApiError ? err.code : null,
              apiStatus: err instanceof ApiError ? err.status : null,
            },
            orgSlug: org.slug,
          });
          setErrorCode(
            err instanceof ApiError && err.code
              ? err.code
              : 'location_import_failed',
          );
          return;
        }
      }

      // Refresh the org list and WAIT for it before navigating. DashboardShell
      // bounces back to the org picker when the slug isn't in the cached
      // my-orgs list (its "org not in my list → redirect" guard), and that
      // cache is stale here (it predates this brand-new org). refetchType:'all'
      // refetches even though no component is observing the query yet.
      await queryClient.invalidateQueries({ queryKey: ['my-orgs'], refetchType: 'all' });

      // Payments step: when the user chose "Connect Stripe", kick off Connect
      // Express onboarding now that the org exists. The hosted Stripe URL
      // returns the user to /orgs/<slug>/settings/billing?stripe=return where
      // the existing return-handler refreshes the mirrored account state.
      // If the Stripe call fails (e.g. unsupported country, Stripe not
      // configured) we fall through to the dashboard so the user isn't
      // stranded — they can retry from Settings → Billing.
      if (draft.connectStripe) {
        const stripeEmail = draft.contactEmail.trim() || auth.user?.profile.email || '';
        const origin = window.location.origin;
        const base = `${origin}/orgs/${encodeURIComponent(org.slug)}/settings/billing`;
        try {
          const { url } = await startStripeOnboarding(token, org.slug, {
            country: draft.countryCode,
            email: stripeEmail,
            refresh_url: `${base}?stripe=refresh`,
            return_url: `${base}?stripe=return`,
          });
          window.location.href = url;
          return;
        } catch (err) {
          // Stripe failures are softer than import failures — the org +
          // members exist, payments are optional. Surface the error so the
          // user knows the redirect didn't happen, but still let them
          // continue to the dashboard via the recovery button.
          clientLog.error('onboarding: stripe onboarding start failed', {
            error: err,
            fields: {
              step: 'onboarding/payments',
              stage: 'stripe_onboarding_start',
              errorType: err instanceof ApiError ? 'api_error' : 'network_or_parse',
              apiCode: err instanceof ApiError ? err.code : null,
              apiStatus: err instanceof ApiError ? err.status : null,
            },
            orgSlug: org.slug,
          });
          setErrorCode(
            err instanceof ApiError && err.code
              ? err.code
              : 'stripe_onboarding_failed',
          );
          return;
        }
      }

      navigate(`/orgs/${org.slug}`);
    } catch (err) {
      // createOrganization itself failed — the org doesn't exist, so there's
      // no slug to recover to. Surface the error and let the user fix and
      // retry from the wizard's Finish button.
      clientLog.error('onboarding: createOrganization failed', {
        error: err,
        fields: {
          step: 'onboarding/submit',
          stage: 'create_organization',
          errorType: err instanceof ApiError ? 'api_error' : 'network_or_parse',
          apiCode: err instanceof ApiError ? err.code : null,
          apiStatus: err instanceof ApiError ? err.status : null,
        },
      });
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
    }
  };

  return (
    <main className="page">
      <picture className="mx-auto block">
        <source srcSet={logoLimeUrl} media="(prefers-color-scheme: dark)" />
        <img
          src={logoUrl}
          alt={t('app.title')}
          className="block h-14 w-auto md:h-[4.5rem]"
        />
      </picture>
      <header className="page__header">
        <h1>{t('onboarding.title')}</h1>
        <Link to="/" className="btn btn--ghost">
          {t('common.cancel')}
        </Link>
      </header>

      <Wizard
        steps={steps}
        draft={draft}
        update={update}
        onSubmit={submit}
        submitErrorCode={errorCode}
        submittingLabelKey="onboarding.submitting"
        jumpToStep={jumpToStep}
      />

      {createdOrgSlug && errorCode && (
        <div
          role="status"
          style={{
            marginTop: '1rem',
            padding: '1rem',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-accent-soft)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          <p style={{ margin: 0 }}>{t('onboarding.recovery.orgCreatedExplain')}</p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => navigate(`/orgs/${createdOrgSlug}`)}
            style={{ alignSelf: 'flex-start' }}
          >
            {t('onboarding.recovery.continueAnyway')}
          </button>
        </div>
      )}

      <LoadingOverlay open={submitting} caption={t('onboarding.submitting')} />
    </main>
  );
}
